import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Lock, RotateCcw, Save, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell, guardianNav } from "@/components/Shell";
import { Loader, Pill } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const box = "w-full px-4 py-3 rounded-xl border text-sm leading-relaxed focus:outline-none focus:border-blue-500";

const Locked = ({ label, text, testId }) => (
  <div className="mt-4">
    <div className="flex items-center gap-2">
      <Lock className="w-3 h-3 text-slate-400" />
      <p className="rule-label">{label}</p>
    </div>
    <p className={`mt-2 ${box} border-slate-200 bg-slate-50 text-slate-500`} data-testid={testId}>
      {text}
    </p>
  </div>
);

export default function AgentPrompts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [openResolved, setOpenResolved] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/agent-prompts");
    setData(data);
    setDrafts(Object.fromEntries(data.agents.map((a) => [a.agent_key, a[data.my_layer] || ""])));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (key, prompt) => {
    setBusy(key);
    try {
      await api.put("/agent-prompts", { agent_key: key, layer: data.my_layer, prompt });
      toast.success(prompt ? "Saved — it is added on top of the platform prompt from the next run" : "Your layer was cleared");
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  if (!data) return <Shell nav={user?.role === "guardian" ? guardianNav : undefined}><Loader label="Loading agent registry…" /></Shell>;

  const mine = data.my_layer;
  const otherLabel = mine === "guardian" ? "Learner's own instructions" : "Guardian instructions";
  const otherKey = mine === "guardian" ? "student" : "guardian";

  return (
    <Shell nav={user?.role === "guardian" ? guardianNav : undefined}>
      <div className="max-w-4xl reveal" data-testid="agents-root">
        <p className="rule-label">Orchestration layer · {data.model}</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">The agent bench</h1>
        <p className="mt-2 text-sm text-slate-500">
          Four layers stack in order — safety, platform, guardian, learner. Each one <span className="font-semibold">adds to</span> the
          ones above it; nothing you write here can override safety or the platform baseline. Students can add their own
          layer to the teaching-facing agents from age {data.student_edit_min_age}
          {data.student_age ? ` (you are ${data.student_age})` : ""}.
        </p>

        <div className="mt-6 card-flat p-6 bg-slate-900 text-white border-slate-800">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-400" />
            <p className="rule-label !text-slate-400">Layer 1 · immutable safety layer</p>
          </div>
          <p className="mt-3 text-sm text-slate-300 leading-relaxed">{data.safety_layer}</p>
        </div>

        <div className="mt-8 space-y-4">
          {data.agents.map((a) => (
            <div key={a.agent_key} className="card-flat p-6" data-testid={`agent-${a.agent_key}`}>
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-display text-lg font-semibold">{a.role}</h3>
                <Pill tone={a.enabled ? "green" : "slate"}>{a.enabled ? "active" : "future phase"}</Pill>
                {a.customised && <Pill tone="amber">customised</Pill>}
                {!a.can_edit && (
                  <Pill tone="slate">
                    <Lock className="w-3 h-3" /> read only
                  </Pill>
                )}
              </div>

              <Locked
                label={`Layer 2 · platform baseline${a.admin_customised ? " (set by admin)" : ""}`}
                text={a.admin}
                testId={`agent-admin-${a.agent_key}`}
              />

              {a[otherKey] ? (
                <Locked label={`Layer ${otherKey === "guardian" ? 3 : 4} · ${otherLabel}`} text={a[otherKey]} testId={`agent-${otherKey}-${a.agent_key}`} />
              ) : null}

              <div className="mt-4">
                <p className="rule-label">
                  Layer {mine === "guardian" ? 3 : 4} · {mine === "guardian" ? "your instructions as guardian" : "your own instructions"}
                </p>
                <textarea
                  data-testid={`agent-prompt-${a.agent_key}`}
                  rows={4}
                  readOnly={!a.can_edit}
                  placeholder={a.can_edit ? "Add anything you want this agent to keep in mind — leave empty for none." : "You cannot edit this agent."}
                  value={drafts[a.agent_key] ?? ""}
                  onChange={(e) => setDrafts({ ...drafts, [a.agent_key]: e.target.value })}
                  className={`mt-2 ${box} ${a.can_edit ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50 text-slate-500"}`}
                />
              </div>

              {a[`${mine}_meta`]?.updated_by && <p className="mt-2 text-xs text-slate-400">Last edited by {a[`${mine}_meta`].updated_by}</p>}

              {a.can_edit && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    data-testid={`agent-save-${a.agent_key}`}
                    onClick={() => save(a.agent_key, drafts[a.agent_key])}
                    disabled={busy === a.agent_key}
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
                  >
                    <Save className="w-3.5 h-3.5" /> Save my layer
                  </button>
                  {a[mine] && (
                    <button
                      data-testid={`agent-revert-${a.agent_key}`}
                      onClick={() => save(a.agent_key, null)}
                      className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-slate-300 text-xs font-semibold hover:border-slate-500 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Clear my layer
                    </button>
                  )}
                </div>
              )}

              <button
                data-testid={`agent-resolved-toggle-${a.agent_key}`}
                onClick={() => setOpenResolved({ ...openResolved, [a.agent_key]: !openResolved[a.agent_key] })}
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900"
              >
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openResolved[a.agent_key] ? "" : "-rotate-90"}`} />
                Resolved prompt sent to the model
              </button>
              {openResolved[a.agent_key] && (
                <pre
                  data-testid={`agent-resolved-${a.agent_key}`}
                  className="mt-3 p-4 rounded-xl bg-slate-900 text-slate-200 text-xs leading-relaxed whitespace-pre-wrap"
                >
                  {a.resolved}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
